import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { lenisStore } from "@/lib/scroll";
import { initGTM } from "@/lib/analytics";
import { staticPageBySlug, serviceBySlug } from "@/lib/siteMap";
import TopBar from "@/components/TopBar";
import Navbar from "@/components/Navbar";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Footer from "@/components/Footer";
import { useFinePointer } from "@/hooks/useCinematic";

// Code splitting por ruta: cada página se descarga solo cuando se visita
const Home = lazy(() => import("@/pages/Home"));
const ServicesHub = lazy(() => import("@/pages/ServicesHub"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Presentacion = lazy(() => import("@/pages/Presentacion"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const BlogHub = lazy(() => import("@/pages/BlogHub"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const About = lazy(() => import("@/pages/About"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const Cursor = lazy(() => import("@/components/Cursor"));

const PageFallback = () => <div className="min-h-screen bg-ink" aria-hidden="true" />;

function RootRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    let target = "es";
    try {
      const saved = localStorage.getItem("dvz-lang");
      if (saved === "en" || saved === "es") target = saved;
      else if (!navigator.language?.startsWith("es")) target = "en";
    } catch { /* noop */ }
    navigate(`/${target}/`, { replace: true });
  }, [navigate]);
  return null;
}

function SlugRouter() {
  const { lang, slug } = useParams();
  const l = lang === "en" ? "en" : "es";
  const st = staticPageBySlug(l, slug);
  if (st) {
    switch (st.id) {
      case "servicios": return <ServicesHub />;
      case "precios": return <Pricing />;
      case "recursos": return <BlogHub />;
      case "nosotros": return <About />;
      case "contacto": return <ContactPage />;
      default: break;
    }
  }
  const svc = serviceBySlug(l, slug);
  if (svc) return <ServicePage key={svc.id} service={svc} />;
  return <NotFound />;
}

function ArticleRouter() {
  const { lang, slug } = useParams();
  const l = lang === "en" ? "en" : "es";
  const st = staticPageBySlug(l, slug);
  if (!st || st.id !== "recursos") return <NotFound />;
  return <ArticlePage />;
}

function LocaleLayout() {
  const { lang } = useParams();
  const location = useLocation();
  const { setLang } = useLanguage();
  const fine = useFinePointer();
  const valid = lang === "en" ? "en" : "es";

  useEffect(() => {
    setLang(valid);
  }, [valid, setLang]);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    let lenis = null;
    let raf = null;
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      import("lenis").then(({ default: Lenis }) => {
        if (cancelled) return;
        lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        lenisStore.instance = lenis;
        const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
        raf = requestAnimationFrame(loop);
      });
    };
    // Diferido: el smooth scroll no compite con el primer render
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(start, { timeout: 1500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(id);
        if (raf) cancelAnimationFrame(raf);
        if (lenis) lenis.destroy();
        lenisStore.instance = null;
      };
    }
    const timer = setTimeout(start, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
      lenisStore.instance = null;
    };
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          if (lenisStore.instance) lenisStore.instance.scrollTo(el, { duration: 1.4 });
          else el.scrollIntoView({ behavior: "smooth" });
        }
      }, 400);
      return () => clearTimeout(timer);
    }
    if (lenisStore.instance) lenisStore.instance.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  if (lang !== "es" && lang !== "en") return <Navigate to="/es/" replace />;

  return (
    <div className="relative min-h-screen bg-ink text-white antialiased">
      {fine && (
        <Suspense fallback={null}>
          <Cursor />
        </Suspense>
      )}
      <div className="noise-overlay" aria-hidden="true" />
      <TopBar />
      <Navbar />
      <Outlet />
      <Footer />
      <WhatsAppFloat />
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{ style: { background: "#0C0C0E", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" } }}
      />
    </div>
  );
}

function App() {
  useEffect(() => {
    // Analytics tras el primer render: no bloquea LCP ni el main thread inicial
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(() => initGTM(), { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(initGTM, 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // Prefetch inteligente post-idle: solo las rutas más probables.
    // Descarga sin montar; no compite con el contenido crítico del Home.
    const prefetch = () => {
      import("@/pages/ServicesHub");
      import("@/pages/Pricing");
      import("@/pages/ContactPage");
    };
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(prefetch, 3500);
    return () => clearTimeout(t);
  }, []);

  return (
    <BrowserRouter>
      <LanguageProvider>
        <Suspense fallback={<PageFallback />}>
          <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/es/presentacion" element={<Presentacion />} />
          <Route path="/:lang" element={<LocaleLayout />}>
            <Route index element={<Home />} />
            <Route path=":slug" element={<SlugRouter />} />
            <Route path=":slug/:article" element={<ArticleRouter />} />
          </Route>
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
